import { useCallback, useEffect, useState } from 'react';
import { ArrowDownRight, ArrowUpRight, X } from 'lucide-react';
import { fetchApi } from '../../api';

interface StockMovementRow {
  id: number;
  delta: number;
  reason: string;
  reasonLabel: string;
  note: string;
  createdAt: string;
  dealId: number | null;
  dealTitle: string | null;
  quantityLabel?: string;
}

interface StockHistoryModalProps {
  productId: number;
  productName: string;
  onClose: () => void;
}

/**
 * The movement history behind one product's remaining quantity.
 *
 * A stock number on its own is a claim. This is the audit trail that makes it
 * checkable: which deal took what, what was returned when a deal was reopened or
 * deleted, and which changes were made by hand.
 */
export default function StockHistoryModal({ productId, productName, onClose }: StockHistoryModalProps) {
  const [movements, setMovements] = useState<StockMovementRow[]>([]);
  const [stockLabel, setStockLabel] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchApi(`/products/${productId}/movements?limit=100`);
      setMovements(Array.isArray(data?.movements) ? data.movements : []);
      setStockLabel(typeof data?.product?.stockLabel === 'string' ? data.product.stockLabel : null);
      setError('');
    } catch (e: any) {
      setError(e?.message || 'Не удалось загрузить историю движения');
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => { void load(); }, [load]);

  const stopPropagation = (event: React.MouseEvent) => event.stopPropagation();

  return (
    <div className="modal open stock-history-modal" onClick={onClose}>
      <div className="modal-card stock-history-card" onClick={stopPropagation} role="dialog" aria-modal="true">
        <div className="modal-head">
          <h3>История движения — {productName}</h3>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Закрыть">
            <X size={16} />
          </button>
        </div>

        {stockLabel && (
          <p className="stock-history-current">
            Текущий остаток: <b>{stockLabel}</b>
          </p>
        )}

        {error && <div className="stock-history-error" role="alert">{error}</div>}

        {loading ? (
          <div className="stock-history-empty">Загрузка истории…</div>
        ) : movements.length === 0 ? (
          <div className="stock-history-empty">
            Движений пока не было. Здесь появятся списания по выигранным сделкам,
            возвраты и ручные корректировки.
          </div>
        ) : (
          <div className="table-wrap stock-history-table">
            <table>
              <thead>
                <tr>
                  <th>Дата</th>
                  <th>Изменение</th>
                  <th>Причина</th>
                  <th>Комментарий</th>
                </tr>
              </thead>
              <tbody>
                {movements.map((movement) => (
                  <tr key={movement.id}>
                    <td className="stock-history-date">
                      {new Date(movement.createdAt).toLocaleString('ru-RU', {
                        day: '2-digit',
                        month: '2-digit',
                        year: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td>
                      <span className={`stock-delta ${movement.delta < 0 ? 'out' : 'in'}`}>
                        {movement.delta < 0 ? <ArrowDownRight size={13} /> : <ArrowUpRight size={13} />}
                        {movement.delta > 0 ? '+' : '−'}{movement.quantityLabel || Math.abs(movement.delta)}
                      </span>
                    </td>
                    <td>
                      <span className="pill gray">{movement.reasonLabel}</span>
                      {movement.dealId && (
                        <span className="stock-history-deal">
                          сделка #{movement.dealId}
                          {movement.dealTitle ? ` «${movement.dealTitle}»` : ''}
                        </span>
                      )}
                    </td>
                    <td className="stock-history-note">{movement.note || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
